import React, { useState } from "react";
import Navbar from "../components/Navigation/Navbar";
import SideNav from "../components/Navigation/SideNav";
import InfoCarousel from "../components/Navigation/InfoCarousel";
import Breadcrumb from "../components/Navigation/Breadcrumb";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Outlet } from "react-router-dom";
import { useTypedSelector } from "../lib/store/store";
import { selectUserState } from "../lib/features/auth/authSlice";
import { useKybVerificationQuery } from "../lib/endpoints/kyb/kyb.api";

/* Mirrors the talent shell's carousel. There is no company-side
   incomplete-profile endpoint, so the tasks come off the user object already
   in the store rather than costing a request on every page. */
const clientTasks = (user, kybStatus) => {
  const location = user?.userLocation;
  const company = user?.userCompany;

  const hasLocation =
    location &&
    (location.city ||
      location.country ||
      location.state ||
      location.street_name);

  /* KYB gates hiring, so it leads. Only the states the client can act on
     become tasks — in-review is news, not something to do. */
  const kyb =
    !kybStatus || kybStatus === "not-started"
      ? "complete your KYB verification"
      : kybStatus === "rejected"
      ? "resubmit your KYB documents"
      : null;

  return [
    kyb,
    !hasLocation && "add your company location",
    !company?.name && "add your company name",
    !company?.about && "add a short company description",
    !company?.address && "add your company address",
    !company?.rc_number && "add your company registration number",
  ].filter(Boolean);
};

/**
 * The client shell (reference: client.tsx). The rail is a full-height sibling
 * of the content column, and the header is sticky inside that column — page
 * scroll is the document's, which is what lets sub-bars stick at top-14.
 *
 * <main> pays the page padding; the width constraint lives in PageShell.
 */
const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useTypedSelector(selectUserState);
  const { kybRecords } = useKybVerificationQuery();
  const kybStatus = kybRecords?.data?.[0]?.status;

  const taskMessages = clientTasks(user, kybStatus).map((label) => (
    <span key={label}>
      <strong className="font-semibold text-brand-orange">
        Action required:
      </strong>{" "}
      Please {label}
    </span>
  ));

  const welcomeMessage = user?.userCompany?.name
    ? `Welcome back, ${user.userCompany.name}!`
    : "Welcome to Droomwork!";

  const kybNote =
    kybStatus === "pending" || kybStatus === "in-review"
      ? ["Your KYB verification is under review."]
      : [];

  const infoItems = [
    welcomeMessage,
    ...taskMessages,
    ...kybNote,
    ...(taskMessages.length === 0 ? ["Your company profile is complete!"] : []),
    "Need help? Contact hello@droomwork.io",
  ];

  return (
    <div className="flex min-h-screen bg-surface">
      <SideNav collapsed={collapsed} setCollapsed={setCollapsed} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Navbar />

        {infoItems.length > 0 && <InfoCarousel items={infoItems} />}

        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto hidden w-full max-w-7xl lg:block">
            <Breadcrumb />
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
